import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Ellipsis, FileText, Search } from "lucide-react";
import { Add } from "@material-symbols-svg/react";
import InfiniteScroll from "react-infinite-scroll-component";

import Header from "src/components/app/layout/Header";
import Input from "src/components/app/Input";
import RecordsUploadBottomSheet from "src/components/app/bottom-sheet/RecordsUploadBottomSheet";
import MoreOptionsBottomSheet from "src/components/app/bottom-sheet/MoreOptionsBottomSheet";
import Shimmer from "src/components/common/Shimmer";
import Loader from "src/components/common/Loader";
import ListCard from "src/components/common/ListCard";

import * as recordsActions from "src/redux/slices/recordsSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { useDebounce } from "src/hooks/useDebounce";
import { DOCUMENT_REVIEW_STATUS } from "src/constants/common";
import { encryptData } from "src/helpers/encrypt-decrypt";
import environment from "src/constants/environment";
import { toast } from "sonner";
import {
  DATE_FORMAT_STRING,
  TIME_FORMAT_STRING,
} from "src/constants/dataTimeFormats";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import Chip from "src/components/app/Chip";
import Button from "src/components/app/Button";

interface RecordsScreenProps {
  supportProgramKey?: string;
}

const RecordsScreen: React.FC<RecordsScreenProps> = ({ supportProgramKey }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getTestTypes, getHealthRecords, activeFilters } = useAppSelector(
    (state) => state.records,
  );

  const [activeRecordType, setActiveRecordType] = useState("all");
  const [showUploadSheet, setShowUploadSheet] = useState(false);
  const [showMoreOptionsSheet, setShowMoreOptionsSheet] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [records, setRecords] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [uploadAgain, setUploadAgain] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { patient } = useAppSelector((state) => state.auth);
  const showBottomBar = useAppSelector(
    (state) => state.config.appLayout.showBottomBar,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    // page view
    analytics.pageView(SCREEN_NAMES.RECORDS_SCREEN);
  }, []);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const fetchTestTypes = useCallback(async () => {
    try {
      await new Promise((resolve, reject) => {
        dispatch(
          recordsActions.getTestTypesRequest({ payload: {}, resolve, reject }),
        );
      });
    } catch (err) {
      console.error("Fetch test types error:", err);
    }
  }, [dispatch]);

  const fetchHealthRecords = useCallback(async () => {
    setIsLoading(true);
    const payload = {
      ...activeFilters,
      page: currentPage.toString(),
      test_type_id: activeRecordType === "all" ? "" : activeRecordType,
      search: debouncedSearchQuery.trim(),
    };

    try {
      const res: any = await new Promise((resolve, reject) => {
        dispatch(
          recordsActions.getHealthRecordsRequest({ payload, resolve, reject }),
        );
      });
      setRecords((prev) =>
        currentPage === 1 ? res?.data : [...prev, ...res?.data],
      );
    } catch (err) {
      console.error("Fetch records error:", err);
      if (currentPage === 1) setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    dispatch,
    activeFilters,
    currentPage,
    activeRecordType,
    debouncedSearchQuery,
  ]);

  useEffect(() => {
    fetchTestTypes();
  }, [fetchTestTypes]);

  useEffect(() => {
    fetchHealthRecords();
  }, [fetchHealthRecords]);

  const loadMoreRecords = useCallback(() => {
    if (!getHealthRecords.isLastPageReached) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [getHealthRecords.isLastPageReached]);

  const handleMoreOptions = useCallback((record: any) => {
    setSelectedRecord(record);
    setShowMoreOptionsSheet(true);
  }, []);

  const handleRecordClick = useCallback(
    (record: any) => {
      const payload = {
        document_name: record.document_name,
        title: record.title,
        document_url: record.document_url[0],
      };
      navigate(`/app/${supportProgramKey}/records/preview`, {
        state: { record: payload },
      });
    },
    [navigate, supportProgramKey],
  );

  const handleFileUpload = async ({
    fileData,
    documentDetails,
  }: {
    fileData: any;
    documentDetails: any;
  }) => {
    setIsFileUploading(true);
    try {
      setShowUploadSheet(false);

      const formData = new FormData();

      let payload = {
        ...(uploadAgain &&
          {
            //   test_type_id: document?.test_type_id,
            //   patient_records_id: document?.patient_records_id,
          }),
        title: documentDetails.documentName,
        test_name: documentDetails.documentType,
        container_account: environment.AZURE_CONTAINER_ACCOUNT,
        document_data: [
          // uploadAgain
          //   ? document?.document_name || ""
          //   : `${
          //       (patient?.data as any)?.patient_id
          //     }-New-Record-${moment().unix()}.pdf`,
          `${
            (patient?.data as any)?.patient_id
          }-New-Record-${moment().unix()}.pdf`,
        ],
        container_name: "patient-reports",
        extension_type:
          fileData?.[0]?.type !== "application/pdf" ? "jpg" : "pdf",
        meta_data: {
          report_status: DOCUMENT_REVIEW_STATUS.UNDER_REVIEW,
          // ...(document?.visit_id && { visit_id: document?.visit_id }),
        },
      };

      // This will change for every PSP
      //   payload["meta_data"][uploadReportType] = "1";

      // Handle file upload
      const files = Array.isArray(fileData) ? fileData : [fileData];

      // Append files to FormData
      for (const file of files) {
        formData.append("file", file);
      }

      formData.append("payload", encryptData(payload));

      new Promise((resolve, reject) => {
        dispatch(
          recordsActions.updateHealthRecordRequest({
            payload: formData,
            resolve,
            reject,
          }),
        );
      })
        .then((res) => {
          // Event: RECORD_UPLOAD_SUCCESS
          analytics.trackEvent(EVENT_NAMES.RECORD_UPLOAD_SUCCESS, {
            screen_name: SCREEN_NAMES.RECORDS_SCREEN,
            support_program_key: supportProgramKey,
            ...payload,
          });
          setShowSuccess(true);
          setCurrentPage(1);
          fetchHealthRecords();
          setIsFileUploading(false);
          toast.success("Record uploaded successfully");
        })
        .catch((error) => {
          // Event: RECORD_UPLOAD_ERROR
          analytics.trackEvent(EVENT_NAMES.RECORD_UPLOAD_ERROR, {
            screen_name: SCREEN_NAMES.RECORDS_SCREEN,
            support_program_key: supportProgramKey,
            ...payload,
          });
          setIsFileUploading(false);
          toast.error("Failed to upload record");
        });
    } catch (error) {
      console.error("Error while uploading Health Record", error);
      toast.error("Failed to upload record");
      setIsFileUploading(false);
    }
  };

  return (
    <div className="relative pt-44">
      <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
        <Header
          title="Records"
          titlePosition="center"
          showBackButton={false}
          // onBackClick={() => navigate(-1)}
        />
        <div className="p-4 space-y-4 bg-[var(--app-primary-background)]">
          <Input
            name="search"
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            fullWidth
            startAdornment={<Search className="w-4 h-4" />}
          />

          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            {/* <div
              onClick={() => {
                setActiveRecordType("all");
                setCurrentPage(1);
              }}
              className={`text-sm px-3 py-1 rounded-full border cursor-pointer whitespace-nowrap ${
                activeRecordType === "all"
                  ? "bg-[var(--app-primary)]/10 border-[var(--app-primary)] text-[var(--app-primary)] bg-[var(--icon-bg-primary)]"
                  : "bg-gray-200 border-gray-300 text-gray-500"
              }`}
            >
              All
            </div> */}
            <Button
              variant={activeRecordType === "all" ? "contained" : "outlined"}
              color={activeRecordType === "all" ? "primary" : "secondary"}
              onClick={() => {
                setActiveRecordType("all");
                setCurrentPage(1);
              }}
              rounded="full"
              size="sm"
            >
              All
            </Button>
            {getTestTypes.isLoading ? (
              <>
                <Shimmer width="100px" height="32px" rounded="rounded-full" />
                <Shimmer width="100px" height="32px" rounded="rounded-full" />
                <Shimmer width="100px" height="32px" rounded="rounded-full" />
                <Shimmer width="100px" height="32px" rounded="rounded-full" />
              </>
            ) : (
              (getTestTypes.data?.test_type || [])
                .slice()
                .sort((a: any, b: any) =>
                  a.test_name.localeCompare(b.test_name),
                )
                .map((type: any) => (
                  <Button
                    variant={
                      activeRecordType === type.test_type_id
                        ? "contained"
                        : "outlined"
                    }
                    color={
                      activeRecordType === type.test_type_id
                        ? "primary"
                        : "secondary"
                    }
                    key={type.test_type_id}
                    onClick={() => {
                      setActiveRecordType(type.test_type_id);
                      setCurrentPage(1);
                    }}
                    rounded="full"
                    size="sm"
                  >
                    {type.test_name}
                  </Button>
                  // <Chip
                  //   label={type.test_name}
                  //   key={type.test_type_id}
                  //   active={activeRecordType === type.test_type_id}
                  //   onClick={() => {
                  //     setActiveRecordType(type.test_type_id);
                  //     setCurrentPage(1);
                  //   }}
                  // />
                ))
            )}
          </div>
        </div>
      </div>

      <InfiniteScroll
        dataLength={records.length}
        next={loadMoreRecords}
        hasMore={!getHealthRecords.isLastPageReached}
        loader={null}
        scrollableTarget="scrollableTarget"
      >
        <div className="p-4 space-y-4 pb-24">
          {records.length ? (
            records.map((record, index) => (
              <ListCard
                key={index}
                icon={
                  <FileText className="w-5 h-5 text-[var(--app-primary)]" />
                }
                title={record.title}
                badge={record.test_name}
                date={moment(record.created_at).format(DATE_FORMAT_STRING)}
                time={moment(record.created_at).format(TIME_FORMAT_STRING)}
                onClick={() => handleRecordClick(record)}
                rightAction={
                  <button
                    className="w-6 h-6 active:bg-gray-100 rounded-md flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoreOptions(record);
                    }}
                  >
                    <Ellipsis className="w-5 h-5" />
                  </button>
                }
              />
            ))
          ) : (
            <div className="text-center text-sm text-gray-400">
              {isLoading ? (
                <div className="space-y-4">
                  <Shimmer width="100%" height="100px" />
                  <Shimmer width="100%" height="100px" />
                  <Shimmer width="100%" height="100px" />
                  <Shimmer width="100%" height="100px" />
                  <Shimmer width="100%" height="100px" />
                </div>
              ) : (
                !getHealthRecords.isLoading && (
                  <div className="text-center text-sm text-gray-400 mt-20">
                    No records found.
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </InfiniteScroll>

      {showBottomBar && (
        <button
          className="fixed bottom-20 right-4 z-20 w-12 h-12 bg-[var(--app-primary)] rounded-full flex items-center justify-center shadow-lg text-white"
          onClick={() => setShowUploadSheet(true)}
        >
          <Add className="w-6 h-6" />
        </button>
      )}

      <RecordsUploadBottomSheet
        showBottomSheet={showUploadSheet}
        setShowBottomSheet={setShowUploadSheet}
        onFileUpload={handleFileUpload}
      />

      <MoreOptionsBottomSheet
        showBottomSheet={showMoreOptionsSheet}
        setShowBottomSheet={setShowMoreOptionsSheet}
        record={selectedRecord}
        showUploadAgain={uploadAgain}
        onUploadAgainClick={() => {
          setUploadAgain(true);
          setShowUploadSheet(true);
        }}
        onDeleteSuccess={() => {
          setCurrentPage(1);
          fetchHealthRecords();
        }}
      />
      {isFileUploading && (
        <div className="fixed top-0 left-0 right-0 z-10 w-full h-full bg-black/50 flex items-center justify-center">
          <Loader size="lg" />
        </div>
      )}
    </div>
  );
};

export default RecordsScreen;
